import type { Bitmap } from "../bitmap/index.ts";
import { bitmapChannels } from "../bitmap/index.ts";
import type { FaceBoundingBox } from "./project.ts";

type PixelRatio = {
	readonly pixelChannelIndexStart: number;
	readonly ratio: number;
	readonly inverseRatio: number;
};

type BitmapMask = {
	// This is internal to this module only atm
	readonly foregroundBoundingBox: {
		readonly left: number;
		readonly right: number;
		readonly top: number;
		readonly bottom: number;
	};
	readonly presentForegroundRatios: readonly PixelRatio[];
	readonly foregroundRatio: readonly (readonly number[])[];
	readonly width: number;
	readonly height: number;
};

function createBitmapMaskFromBitmap({
	data,
	width,
	height,
}: Bitmap): BitmapMask {
	const foregroundRatio: number[][] = new Array<number[]>(width);
	let boundingLeft = width - 1;
	let boundingRight = 0;
	let boundingTop = height - 1;
	let boundingBottom = 0;

	const presentForegroundRatios: PixelRatio[] = [];
	for (let x = 0; x < width; x++) {
		foregroundRatio[x] = new Array<number>(height);
		for (let y = 0; y < height; y++) {
			const idx = (width * y + x) << 2;

			// Assume each pixel the same because grayscale
			const pixelForegroundRatio = data[idx] / 255;

			foregroundRatio[x][y] = pixelForegroundRatio;

			if (pixelForegroundRatio !== 0) {
				presentForegroundRatios.push({
					pixelChannelIndexStart: idx,
					ratio: pixelForegroundRatio,
					inverseRatio: 1 - pixelForegroundRatio,
				});

				if (x < boundingLeft) {
					boundingLeft = x;
				}
				if (x > boundingRight) {
					boundingRight = x;
				}
				if (y < boundingTop) {
					boundingTop = y;
				}
				if (y > boundingBottom) {
					boundingBottom = y;
				}
			}
		}
	}

	presentForegroundRatios.sort(
		(a, b) => a.pixelChannelIndexStart - b.pixelChannelIndexStart,
	);

	return {
		width,
		height,
		presentForegroundRatios,
		foregroundRatio,
		foregroundBoundingBox: {
			left: Math.min(boundingLeft, boundingRight),
			top: Math.min(boundingTop, boundingBottom),
			right: boundingRight,
			bottom: boundingBottom,
		},
	};
}

// TODO: Could be optimised based on bg mask bounding masks
function createFacesMask(
	backgroundMask: BitmapMask,
	faceBoundingBoxes: readonly Pick<
		FaceBoundingBox,
		"x" | "y" | "width" | "height"
	>[],
): BitmapMask {
	if (faceBoundingBoxes.length === 0) {
		return backgroundMask;
	}

	const {
		width,
		height,
		foregroundBoundingBox: backgroundForegroundBoundingBox,
	} = backgroundMask;

	// If there's no foreground then use the whole face area
	if (
		backgroundForegroundBoundingBox.left >=
			backgroundForegroundBoundingBox.right ||
		backgroundForegroundBoundingBox.top >=
			backgroundForegroundBoundingBox.bottom
	) {
		return {
			width,
			height,
			presentForegroundRatios: Array(width * height)
				.fill(undefined)
				.map((_, i) => ({
					ratio: 1,
					inverseRatio: 0,
					pixelChannelIndexStart: i * bitmapChannels,
				})),
			foregroundRatio: new Array<number[]>(width).fill(
				new Array<number>(height).fill(1),
			),
			foregroundBoundingBox: {
				left: 0,
				top: 0,
				right: width - 1,
				bottom: height - 1,
			},
		};
	}

	const faceBoundingBoxPositions = faceBoundingBoxes.map((bb) => {
		const x = Math.round(bb.x * width);
		const y = Math.round(bb.y * height);
		return {
			x,
			y,
			right: Math.round((bb.x + bb.width) * width),
			bottom: Math.round((bb.y + bb.height) * height),
		};
	});

	const foregroundRatio: number[][] = new Array<number[]>(width);
	let boundingLeft = width - 1;
	let boundingRight = 0;
	let boundingTop = height - 1;
	let boundingBottom = 0;

	// TODO: For more efficiency could get one of these from previous bitmap mask.
	// note: could save a columnIndex on data structure to help
	const emptyColumn = Array<number>(height).fill(0);

	const midColForegroundInitial = [
		...Array<number>(backgroundForegroundBoundingBox.top).fill(0),
		...Array<number>(
			backgroundForegroundBoundingBox.bottom -
				backgroundForegroundBoundingBox.top -
				1,
		).fill(0),
		...Array<number>(height - backgroundForegroundBoundingBox.bottom - 1).fill(
			0,
		),
	];

	const presentForegroundRatios: PixelRatio[] = [];
	for (let x = 0; x < width; x++) {
		if (
			x < backgroundForegroundBoundingBox.left ||
			x > backgroundForegroundBoundingBox.right
		) {
			foregroundRatio[x] = emptyColumn;
			continue;
		}

		foregroundRatio[x] = midColForegroundInitial.slice();
		for (
			let y = backgroundForegroundBoundingBox.top;
			y <= backgroundForegroundBoundingBox.bottom;
			y++
		) {
			// Assume each pixel the same because grayscale
			const pixelForegroundRatio =
				faceBoundingBoxPositions.length === 0 ||
				faceBoundingBoxPositions.some(
					(bb) => x >= bb.x && x <= bb.right && y >= bb.y && y <= bb.bottom,
				)
					? backgroundMask.foregroundRatio[x][y]
					: 0;

			foregroundRatio[x][y] = pixelForegroundRatio;

			if (pixelForegroundRatio !== 0) {
				const idx = (width * y + x) << 2;
				presentForegroundRatios.push({
					pixelChannelIndexStart: idx,
					ratio: pixelForegroundRatio,
					inverseRatio: 1 - pixelForegroundRatio,
				});

				if (x < boundingLeft) {
					boundingLeft = x;
				}
				if (x > boundingRight) {
					boundingRight = x;
				}
				if (y < boundingTop) {
					boundingTop = y;
				}
				if (y > boundingBottom) {
					boundingBottom = y;
				}
			}
		}
	}

	presentForegroundRatios.sort(
		(a, b) => a.pixelChannelIndexStart - b.pixelChannelIndexStart,
	);

	return {
		width,
		height,
		foregroundRatio,
		presentForegroundRatios,
		foregroundBoundingBox: {
			// left: Math.min(boundingLeft, boundingRight),
			// top: Math.min(boundingTop, boundingBottom),
			left: boundingLeft,
			top: boundingTop,
			right: boundingRight,
			bottom: boundingBottom,
		},
	};
}

export { createFacesMask, createBitmapMaskFromBitmap };
export type { BitmapMask };
